import { VALID_PLACEMENTS } from '../Header/Dropdown/Drawer/StyledHeaderDrawer';
import { themeMerge } from '../Utils';

import IThemeInterface, { breakPointSizes, edgeSize, zIndices } from './common';

const theme: IThemeInterface = themeMerge({
  name: 'jenius',

  global: {
    breakPointSizes,

    colors: {
      primaryPurple: '#521B74',
      secondaryPurple: '#6A169D',
      secondaryDarkPurple: '#EDEEFB',
      primaryDarkPurple: '#391351',
      primaryGreen: '#B1E271',
      primaryWhite: '#FFFDF2',
      secondaryPink: '#D4A9FF',
      secondaryBlue: '#91CAFF',
      secondaryRed: '#F16F53',
      secondaryYellow: '#FCD157',
      secondaryOrange: '#FFA24C',
      secondaryDarkGreen: '#062E30',
      secondaryMidGreen: '#217A7F',
      secondaryLightGray: '#E9E8E1',
      secondaryMidGray: '#C7C6BF',
      secondaryDarkGray: '#8A8985',

      secondary: '#1780A3',
      secondary120: '#105A72',
      secondary20: '#D1E6ED',
      secondary10: '#E8F2F6',
      link: '#521B74',
      link120: '#1A1C1D',
      link20: '#D3D4D4',
      link10: '#E9E9EA',
      info: '#4B54D8',
      info120: '#0B4A5F',
      info20: '#CFE1E7',
      info10: '#E7F0F3',
      success: '#175559',
      success120: '#3A7670',
      success20: '#DDEEEC',
      success10: '#EEF6F5',
      warning: '#FCD157',
      warning120: '#B3A01E',
      warning20: '#FFFAD5',
      warning10: '#FFFCEA',
      error: '#C0524D',
      error120: '#871D38',
      error20: '#F3D4DC',
      error10: '#F9EAED',
      focus: '#007AFF',

      purpleTransparent: 'rgba(82, 27, 116, 0.2)',
      purple700: '#5119C2',
      purple600: '#7C41D8',
      purple500: '#A96EED',
      purple400: '#D4A9FF',
      purple300: '#E5B9FF',
      purple200: '#F6D9FF',
      purple100: '#FCF1FF',
      green700: '#062E30',
      green600: '#00464A',
      green500: '#0A5F64',
      green400: '#217A7F',
      green300: '#5BB6BB',
      green200: '#98D7DB',
      green100: '#D2F2F3',
      gray700: '#373632',
      gray600: '#54534E',
      gray500: '#6C6B66',
      gray400: '#C8CED4',
      gray300: '#B0AFA8',
      gray200: '#C7C6BF',
      gray100: '#F7F8F9',
      blue700: '#3723C2',
      blue600: '#4B54D8',
      blue500: '#5F8CEC',
      blue400: '#91CAFF',
      blue300: '#ACD7FF',
      blue200: '#CDE7FF',
      blue100: '#DEEFFF',
      red700: '#5E1740',
      red600: '#8E3447',
      red500: '#C0524D',
      red400: '#F16F53',
      red300: '#FF947E',
      red200: '#FFB9AA',
      red100: '#FFD4CB',
      orange700: '#7F3126',
      orange600: '#964C41',
      orange500: '#CB7746',
      orange400: '#FFA24C',
      orange300: '#FFB876',
      orange200: '#FFD0A4',
      orange100: '#FFE5CC',
      yellow700: '#824E48',
      yellow600: '#94614A',
      yellow500: '#C99A50',
      yellow400: '#FCD157',
      yellow300: '#FFE089',
      yellow200: '#FFEAAF',
      yellow100: '#FFF6DC',

      black: '#212529',
      white: '#fff',
    },

    edgeSize,

    elevation: {
      '1x': { color: 'rgba(33, 37, 41, 0.16)', shadow: '0px 1px 8px 1px' },
      '2x': { color: 'rgba(33, 37, 41, 0.16)', shadow: '0px 6px 16px 1px' },
    },

    font: {
      family: {
        roboto: 'roboto',
        lato: 'lato',
      },

      size: {
        heroLarge: '3.2rem',
        heroLargeMobile: '2.8rem',
        heroMedium: '2.4rem',
        heroSmall: '2rem',
        body: '1.6rem',
        bodyLarge: '1.8rem',
        bodyMedium: '1.4rem',
        bodySmall: '1.2rem',
      },
    },

    outline: {
      color: 'focus',
      size: 'triple',
      style: 'solid',
    },

    zIndex: zIndices
  },

  banner: {
    info: {
      backgroundColor: 'blue100',
      border: { side: 'bottom', color: 'info' },
      font: { color: 'black' },
      padding: { horizontal: '3x', vertical: '2x' },
      inlineLink: {
        color: 'black',
        hoverColor: 'black',
      },
      closeIcon: {
        color: 'black'
      },
    },
    warning: {
      font: { color: 'black' },
      backgroundColor: 'yellow100',
      border: { side: 'bottom', color: 'warning' },
      inlineLink: {
        color: 'black',
        hoverColor: 'black',
      },
      closeIcon: {
        color: 'black'
      },
    },
    success: {
      font: { color: 'black' },
      backgroundColor: 'green100',
      border: { side: 'bottom', color: 'success' },
      inlineLink: {
        color: 'black',
        hoverColor: 'black',
      },
      closeIcon: {
        color: 'black'
      },
    },
    error: {
      font: { color: 'black' },
      backgroundColor: 'red100',
      border: { side: 'bottom', color: 'error' },
      inlineLink: {
        color: 'black',
        hoverColor: 'black',
      },
      closeIcon: {
        color: 'black'
      },
    },
  },

  button: {
    base: {
      background: 'primaryPurple',
      border: { size: '1px', color: 'primaryPurple' },
      borderRadius: '3rem',
      font: {
        color: 'white',
        size: 'bodyLarge',
        weight: '600',
        lineHeight: '1.55',
      },
      padding: { vertical: '1.75x', horizontal: '3x' },
      width: { min: '19.2rem' },
      outline: 'none',
      loadingIconInsideButton: true,
      spinner: {
        color: {
          circleOutline: 'secondaryPurple',
          animatedOutline: 'white',
        },
      },
    },
    overrides: {
      primary: {
        state: {
          hover: {
            background: 'secondaryPurple'
          },
          active: {
            background: 'secondaryPurple'
          },
          disabled: {
            background: 'gray200',
            border: { color: 'gray200' },
            font: { color: 'gray600', weight: '600' }
          },
          focus: {
            shadow: { shadow: '0 0 0 3px', color: 'focus' },
          }
        }
      },
      secondary: {
        base: {
          background: 'white',
          border: { size: '2px', color: 'primaryPurple' },
          font: { color: 'primaryPurple' },
          spinner: {
            color: {
              circleOutline: 'purpleTransparent',
              animatedOutline: 'primaryPurple',
            },
          },
        },
        state: {
          hover: {
            background: 'primaryPurple',
            border: { color: 'primaryPurple' },
            font: { color: 'white' },
          },
          active: {
            background: 'primaryPurple',
            border: { color: 'primaryPurple' },
            font: { color: 'white' },
          },
          disabled: {
            background: 'gray200',
            border: { color: 'gray200' },
            font: { color: 'gray600' },
          },
          focus: {
            shadow: { shadow: '0 0 0 3px', color: 'focus' },
          }
        }
      },
      text: {
        state: {
          hover: {
            font: { color: 'secondaryPurple' },
          },
          focus: {
            shadow: { shadow: '0 0 0 3px', color: 'focus' },
            font: {
              textDecoration: 'underline',
            },
          },
        },
      },
      destructive: {
        base: {
          background: 'error',
          border: { size: '1px', color: 'error' },
          font: { color: 'white' },
        },
        state: {
          hover: {
            background: 'error120',
            border: { color: 'error120' }
          },
          active: {
            background: 'error120',
            border: { color: 'error120' }
          },
          disabled: {
            background: 'gray200',
            border: { color: 'gray200' },
            font: { color: 'gray600' }
          },
          focus: {
            shadow: { shadow: '0 0 0 3px', color: 'focus' },
          }
        }
      },
      action: {
        base: {
          background: 'transparent',
          shadow: 'none',
          font: { color: 'primaryPurple', textTransform: 'none', lineHeight: '1.45', weight: 'normal', letterSpacing: '0' },
          borderRadius: '100px',
          border: { color: 'primaryPurple', size: '2px' },
          width: { min: 'auto', base: 'auto' },
        },
        state: {
          active: {
            background: 'primaryPurple',
            border: { color: 'primaryPurple' },
            font: { color: 'white' }
          },
          disabled: {
            border: { color: 'gray300' },
            font: { color: 'gray500' },
          },
          hover: {
            border: { color: 'primaryPurple' }
          },
          focus: {
            border: { color: 'gray400' },
            outline: 'none',
            shadow: [
              { color: 'white', inset: false, shadow: '0 0 0 2px' },
              { color: 'focus', inset: false, shadow: '0 0 0 4px' },
            ],
          },
          selected: {
            background: 'primaryPurple',
            border: { color: 'primaryPurple' },
            font: { color: 'white', textTransform: 'none' },
          }
        },
      },
      link: {
        base: {
          background: 'none',
          border: { size: '0' },
          padding: { horizontal: '0', vertical: '1.25x' },
          width: { base: 'auto', min: 'auto' },
          font: {
            color: 'primaryPurple',
            textDecoration: 'underline',
            lineHeight: '1.33',
            weight: '400'
          },
        },
        state: {
          hover: {
            font: { color: 'primaryDarkPurple' }
          },
          active: {
            font: { color: 'secondaryDarkGray' }
          },
          disabled: {
            font: { color: 'gray600', textDecoration: 'none' }
          },
          focus: {
            shadow: { shadow: '0 0 0 3px', color: 'focus' },
          }
        }
      },
      inverse: {
        base: {
          background: 'white',
          border: 'none',
          font: { color: 'primaryPurple' },
        },
        state: {
          hover: {
            background: 'primaryWhite',
            font: { color: 'secondaryPurple' },
          },
          active: {
            background: 'primaryWhite',
            font: { color: 'secondaryPurple' },
          },
          disabled: {
            background: 'gray200',
            border: { color: 'gray200' },
            font: { color: 'gray600' }
          },
          focus: {
            shadow: { shadow: '0 0 0 3px', color: 'focus' },
          }
        }
      },
    }
  },

  barProgress: {
    borderRadius: '10rem',
    track: {
      height: '0.8rem',
      color: {
        base: 'gray400',
        progress: 'success'
      }
    }
  },

  circleProgress: {
    backgroundPathColor: 'gray400',
    percentagePathColor: 'success',
    label: {
      percentage: {
        number: {
          size: 'heroLarge',
          lineHeight: '1.25',
          color: 'black',
          family: 'roboto',
          weight: '300',
          display: 'flex',
        },
        sign: {
          size: 'heroMedium',
          weight: '400',
          verticalAlign: 'middle',
          alignSelf: 'center',
          padding: { left: 'double' }
        }
      },
      content: {
        family: 'roboto',
        size: 'bodyMedium',
        color: 'gray600',
        lineHeight: '1.425',
      },
    },
  },

  containerSize: {
    full: '100%',
    narrow: '51.2rem', // 512px
    'narrow-2': '70.4rem', // 704px
    wide: '89.6rem', // 896px
    max: '128rem', // 1280px
  },

  descriptionList: {
    term: {
      color: 'black',
      size: 'body',
      margin: { right: '3x' },
      lineHeight: '1.5',
      family: 'roboto',
      weight: '600',
    },

    detail: {
      color: 'black',
      size: 'body',
      lineHeight: '1.5',
      family: 'roboto',
      weight: '400',
    },

    variant: {
      large: {
        term: {
          lineHeight: '1.45',
          size: 'bodyMedium',
          family: 'roboto',
          weight: '600',
        },
        detail: {
          size: 'heroLarge',
          lineHeight: '1.25',
          family: 'roboto',
          weight: '600',
        },
      },

      small: {
        term: {
          lineHeight: '1.45',
          size: 'bodyMedium',
          family: 'roboto',
          weight: '600',
        },
        detail: {
          size: 'heroMedium',
          lineHeight: '1.35',
          family: 'roboto',
          weight: '600',
        },
      },
    },
  },

  form: {
    checkbox: {
      backgroundColor: 'white',
      border: { color: 'gray500' },
      label: {
        verticalOffset: 'translateY(-3px)',
        font: { color: 'black', size: 'bodyLarge', lineHeight: '1.556' },
      },
      icon: {
        checkedIconType: 'checkbox-done',
        size: '2.25x',
      },
      size: '2.5x',
      round: 'quadruple',
      margin: { right: '1.25x', left: 'double' },
      padding: { vertical: '1x' },

      state: {
        checked: {
          backgroundColor: 'primaryPurple',
          border: { color: 'primaryPurple' },
        },

        checkedHover: {
          backgroundColor: 'secondaryDarkGreen',
          border: { color: 'secondaryDarkGreen' },
        },

        disabled: {
          backgroundColor: 'gray100',
          border: { color: 'gray500' },
          label: { font: { color: 'gray600' } },
        },

        disabledChecked: {
          backgroundColor: 'gray100',
          icon: { color: 'gray500', size: '2.25x' },
        },

        error: {
          border: { size: 'double', color: 'error' },
        },

        focus: {
          focusPosition: 'labelAndIndicator',
          outline: { size: 'triple' },
        },

        hover: {
          border: { color: 'secondaryDarkGreen' },
        },
      },
    },

    errorText: {
      checkboxAndRadioErrorIndention: false,
      font: {
        color: 'error',
        size: 'bodyMedium',
        lineHeight: '1.4',
      },
      margin: { top: '1x' },
    },

    helperText: {
      font: {
        color: {
          default: 'black',
          disabled: 'gray600',
          readOnly: 'gray600',
        },
        size: 'bodyMedium',
        weight: '400',
        lineHeight: '1.4',
      },
      margin: { top: '1x' },
    },

    label: {
      font: {
        color: {
          default: 'black',
          disabled: 'gray600',
          readOnly: 'gray600',
        },
        size: 'body',
        weight: '400',
        lineHeight: '1.5',
      },
      margin: { bottom: '1x' },
    },

    layout: {
      wrapper: {
        row: { margin: { bottom: '3x' } },
        stackRow: {
          margin: {
            base: { right: '0' },
            small: { right: '3x' },
          },
        },
      },
    },

    legend: {
      font: {
        size: 'body',
        color: 'black',
        lineHeight: '1.5',
      },
      margin: { bottom: '1x' },
    },

    input: {
      backgroundColor: 'white',
      border: { color: 'gray500', side: 'all', size: 'single' },
      iconColor: 'gray600',
      font: { color: 'black', lineHeight: '1.5', size: 'bodyLarge' },
      padding: { horizontal: '2x', top: '1.35rem', bottom: '1.35rem' },
      placeholder: 'gray600',
      round: 'double',
      withTooltipVerticalOffset: '-2px',

      state: {
        hover: {
          border: { color: 'gray700' }
        },

        focus: {
          outline: 'none',
          shadow: { color: 'focus', inset: false, shadow: '0 0 0 3px' }
        },
        error: {
          border: { color: 'error' },
          shadow: { color: 'error', shadow: '0 0 0 1px', inset: true },
        },

        disabled: {
          border: { color: 'gray300', side: 'all', size: 'single' },
          backgroundColor: 'gray100',
          placeholder: 'gray600',
        },
      }
    },

    select: {
      icon: {
        type: 'select-input-arrow',
        size: '3x',
        color: 'black',
        errorColor: 'black',
      },
    },

    textarea: {
      backgroundColor: 'white',
      border: { color: 'gray500', side: 'all', size: 'single' },
      font: { color: 'black', size: 'bodyLarge', lineHeight: '1.55' },
      padding: { horizontal: '2x', top: '1.75x', bottom: '1.75x' },
      round: 'double',

      state: {
        hover: {
          border: { color: 'gray700' },
        },

        focus: {
          outline: 'none',
          shadow: { color: 'focus', inset: false, shadow: '0 0 0 3px' }
        },

        error: {
          border: { color: 'error' },
        },

        disabled: {
          border: { color: 'gray300', side: 'all', size: 'single' },
          backgroundColor: 'gray100',
          placeholder: 'gray600',
        },
      },
    },

    radio: {
      badge: {
        backgroundColor: 'white',
        border: { color: 'gray500' },
        padding: { vertical: '1.5x', horizontal: '2x' },
        round: '2px',
        width: { min: '8.6rem' },
        font: { color: 'black', size: 'bodyLarge', lineHeight: '1.556' },

        state: {
          checked: {
            backgroundColor: 'purple100',
            border: { color: 'primaryPurple', size: '0.3rem' },
          },

          checkedHover: {
            backgroundColor: 'purple100',
            border: { color: 'primaryPurple', size: '0.3rem' },
          },

          disabled: {
            backgroundColor: 'gray100',
            border: { color: 'gray100' },
            font: { color: 'gray600' },
          },

          disabledChecked: {
            backgroundColor: 'gray200',
            border: { color: 'gray200' },
            font: { color: 'gray600' },
            shadow: 'none',
          },

          error: {
            border: { color: 'error' },
            shadow: { inset: true, color: 'error', shadow: '0 0 0 1px' },
          },

          hover: { border: { color: 'secondaryDarkGreen' } },
        },
      },

      button: {
        backgroundColor: 'white',
        checkedStyle: 'solid',
        checkedBgColor: 'secondaryMidGreen',
        label: {
          font: { color: 'black', lineHeight: '1.556', size: 'bodyLarge' },
          margin: { top: '0.2rem', left: '4x' },
          padding: { top: '1x', bottom: '1x' },
        },
        size: '3x',
        border: { color: 'gray500' },

        state: {
          checked: {
            shadow: { color: 'white', inset: true, shadow: '0 0 0 3px' },
            border: { color: 'secondaryDarkGreen' },
            label: { font: { color: 'black' } },
          },

          checkedHover: {
            border: { color: 'secondaryDarkGreen' },
            backgroundColor: 'secondaryDarkGreen',
          },

          disabled: {
            border: { color: 'gray500' },
            backgroundColor: 'gray100',
            label: { font: { color: 'gray600' } },
          },

          disabledChecked: {
            backgroundColor: 'gray500',
            shadow: { color: 'gray100', inset: true, shadow: '0 0 0 3px' },
          },

          error: {
            border: { color: 'error', size: 'double' },
          },

          focus: {
            focusPosition: 'labelAndIndicator',
          },

          hover: { border: { color: 'secondaryDarkGreen' } },
        },
      },

      card: {
        backgroundColor: 'white',
        border: { color: 'gray400' },
        round: '0.625x',

        state: {
          checked: {
            backgroundColor: 'purple100',
            border: { color: 'primaryPurple', size: 'triple' },
          },

          disabled: {
            backgroundColor: 'gray100',
            border: { size: 'single', color: 'gray200' },
          },

          focus: {
            border: { color: 'focus' },
            shadow: { color: 'focus', shadow: '0 0 0 2px' },
          },

          error: {
            border: { color: 'error' },
            shadow: { color: 'error', shadow: '0 0 0 2px' },
            text: { margin: { top: '3x' } }
          },

          hover: {
            border: { color: 'secondaryMidGreen' },
            shadow: 'none'
          },
        },
      },

      toggle: {
        label: {
          color: 'black',
          size: 'bodyLarge',
          lineHeight: '1.556'
        },

        button: {
          height: '3x',
          width: '6x',
          margin: { right: '1.5x' },
          padding: { vertical: 'triple', horizontal: 'triple' },
          border: { color: 'gray500' },
          backgroundColor: 'white',
        },

        slidingIndicator: {
          size: '2.25x',
          color: 'gray500',
        },

        state: {
          checked: {
            button: {
              border: { color: 'primaryPurple' },
              backgroundColor: 'purple100',
            },
            slidingIndicator: {
              color: 'white',
            },
          },

          checkedHover: {
            button: {
              border: { color: 'primaryPurple' },
              backgroundColor: 'purple100',
            },
          },

          hover: {
            button: {
              border: { color: 'primaryPurple' }
            },
            slidingIndicator: {
              color: 'primaryPurple',
            },
          },

          disabled: {
            button: {
              border: { color: 'gray400' },
              backgroundColor: 'gray100',
            },
            slidingIndicator: {
              color: 'gray400',
            },
          },

          disabledChecked: {
            button: {
              border: { color: 'gray300' },
              backgroundColor: 'gray200',
            },
            slidingIndicator: {
              color: 'white',
            }
          },

          focus: {
            label: { outline: { offset: 'double', style: 'auto' } },
          },
        },
      }
    },

    slider: {
      thumb: {
        color: { default: 'primaryPurple', disabled: 'gray500' },
        height: 36,
        width: 36,
      },
      track: {
        color: {
          base: { default: 'gray400', disabled: 'gray500' },
          progress: { default: 'primaryPurple', disabled: 'gray500' }
        },
        thickness: 8,
        borderRadius: '10px',
      },
      unitLabels: {
        color: { default: 'black', disabled: 'gray600' },
        margin: { top: '2x' },
      },
    },
  },

  heading: {
    default: {
      color: 'black',
      family: 'lato',
      weight: '600',
      lineHeight: '1.25',
    },

    level: {
      h1: {
        size: { base: 'heroLargeMobile', medium: 'heroLarge' },
        lineHeight: { base: '1.3', medium: '1.25' },
      },

      h2: {
        size: { base: 'heroMedium' },
      },

      h3: {
        size: { base: 'heroSmall' },
      },

      h4: {
        size: { base: 'bodyLarge' },
      },
      h5: {/* level specific styles */ },
      h6: {/* level specific styles */ },
    },
  },

  layout: {
    header: {
      backgroundColor: 'primaryPurple',
      border: { size: '1px', side: 'bottom', color: 'gray400' },
      height: { base: '6.4rem', small: '8rem' },
      padding: { horizontal: '3x' },
      dropdown: {
        shadow: '1x',
        backgroundColor: 'white',
        width: { base: '100%', small: '25.6rem' },
        border: 'none',
        padding: { vertical: 'quadruple' },
        icon: {
          desktop: {
            size: '2.75x',
            color: 'black',
            type: 'arrow-bottom',
          },
          mobile: {
            open: {
              type: 'menu',
              color: 'white',
              size: '3x'
            },
            close: {
              size: '3x',
              color: 'white',
              type: 'close'
            }
          },
        },
        button: {
          font: {
            color: 'black',
            size: 'body',
            lineHeight: '1.5'
          },
          state: {
            hover: { font: { color: 'primaryPurple' } },
          },
        },
        link: {
          divider: {
            placement: VALID_PLACEMENTS.lastChildOnly,
            height: '1px',
            backgroundColor: 'gray400',
            width: { base: '100%' }
          },
          border: { size: 'triple', color: 'transparent' },
          font: {
            color: 'black',
            size: 'body',
            lineHeight: '1.5',
            align: { base: 'left' },
          },
          padding: { vertical: '1.3rem', horizontal: '3x' },
          state: {
            hover: {
              backgroundColor: 'green100',
              font: {
                color: 'secondaryMidGreen',
              },
            },
            focus: {
              border: { size: 'triple', color: 'focus' },
            },
          },
        }
      },
      link: {
        padding: { vertical: '1.35rem' },
        font: {
          color: 'white',
          lineHeight: '1.5',
          size: 'body',
        },
        margin: {
          right: '3x',
        },
        state: {
          hover: { font: { color: 'white' } },
          current: { font: { color: 'white', family: 'roboto', weight: '600' } },
        },
      },
    },
    footer: {
      backgroundColor: 'primaryPurple',
      border: { size: '1px', side: 'bottom', color: 'gray400' },
      height: { base: '6.4rem', small: '8rem' },
      padding: { horizontal: '3x' },
      link: {
        padding: { vertical: '1.35rem' },
        font: {
          color: 'white',
          lineHeight: '1.5',
          size: 'body',
          weight: '700'
        },
        margin: {
          right: '2x',
        },
        state: {
          hover: { font: { color: 'white' } },
        },
      },
    },
  },

  link: {
    font: { color: 'primaryPurple' },
    inline: {
      state: {
        hover: { color: 'primaryDarkPurple' },
      }
    }
  },

  list: {
    margin: { left: '3x' },

    listItem: {
      common: {
        margin: { bottom: '1x' },
        font: {
          family: 'roboto',
          lineHeight: '1.5',
          size: 'body',
          color: 'black',
          weight: '400'
        },
      },

      bullet: {
        backgroundColor: 'black',
        listStyleSpacing: '2.5rem',
        size: '0.75x',
      },

      icon: {
        listStyleSpacing: '1.47rem',
        size: '2.5x',
        type: 'done',
        color: 'black',
      },

      number: {
        listStyleSpacing: '3.2rem',
        font: {
          color: 'black',
        },
      },
    },
  },

  modal: {
    overlay: {
      backgroundColor: 'rgba(8, 8, 8, 0.85)',
      padding: {
        base: { top: '9.6rem' },
        small: { top: '11.2rem' },
      },
    },
    card: {
      backgroundColor: 'white',
      padding: {
        base: {
          top: '7.5x',
          bottom: '4x',
          horizontal: '4x',
        },
        small: {
          bottom: '6x',
          horizontal: '6x',
        },
      },
      round: '8px',
      shadow: '0 single 1x single black',
    },
    closeIcon: {
      type: 'close',
      size: '1.75x',
      color: 'black',
      padding: '2.5x'
    },
  },

  spinner: {
    size: '12rem',
    iconStrokeWidth: 1,
    withOutline: true,
    strokeLinecap: 'round',
    color: {
      animatedOutline: 'primaryPurple',
      circleOutline: 'purpleTransparent',
    },
  },

  table: {
    borderSpacing: '2.4rem',
    padding: { vertical: '2.5x' },

    tableHeader: {
      backgroundColor: 'gray100',
      rowDivider: 'gray400',
      padding: { vertical: '1.75x', horizontal: '2x' },
      font: {
        size: 'body',
        lineHeight: '1.25',
        weight: '600',
      },
    },

    subHeader: {
      backgroundColor: 'gray200',
      border: { side: 'bottom', color: 'gray400' },
      font: {
        size: 'body',
        weight: '600',
      },
    },

    tableBody: {
      rowDivider: 'gray400',
      padding: '2x',
      font: {
        size: 'body',
        lineHeight: '1.5',
        weight: '400',
      },
    },
  },

  tooltip: {
    pane: {
      background: 'white',
      padding: { horizontal: '2x', vertical: '1.5x' },
      shadow: '0px 1px 8px 1px rgba(33, 37, 41, 0.16)',
      round: '0.625x',
      border: { color: 'gray400' },
      position: 'top',

      font: {
        size: 'body',
        color: 'black',
        lineHeight: '1.5',
      },
    },

    icon: {
      size: '2.5x',
      color: 'link',
      hover: 'link120',
      margin: { horizontal: 'quadruple' }
    },
  },

  typography: {
    text: {
      body: {
        default: {
          color: 'black',
          lineHeight: '1.5',
          family: 'roboto',
        },
      },
    },

    pill: {
      default: {
        padding: { vertical: 'quadruple', horizontal: '1x' },
        round: 'double',
        backgroundColor: 'primaryPurple',
        font: {
          transform: 'capitalize',
          color: 'white',
          size: 'bodyMedium',
          lineHeight: '1.45',
          weight: '700',
        },
      },
      neutral: {
        backgroundColor: 'gray200',
        font: { color: 'black' },
      },
      success: { backgroundColor: 'success' },
      warning: {
        backgroundColor: 'warning',
        font: { color: 'black' },
      },
      error: { backgroundColor: 'error' },
      info: { backgroundColor: 'info' },
    },
  },

  datePicker: {
    input: {
      // tslint:disable:max-line-length
      icon: (color: string) => `"data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M19 19H5V8H19V19ZM16 1V3H8V1H6V3H5C3.89 3 3 3.89 3 5V19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21H19C19.5304 21 20.0391 20.7893 20.4142 20.4142C20.7893 20.0391 21 19.5304 21 19V5C21 3.89 20.1 3 19 3H18V1H16ZM17 12H12V17H17V12Z' fill='${encodeURIComponent(color)}'/%3E%3C/svg%3E%0A"`,
      iconColor: {
        base: 'black',
        disabled: 'gray400'
      },
      iconSize: {
        width: '2.4rem',
        height: '2.4rem'
      },
      valueFormat: 'MM/DD/YYYY',
      width: '31.2rem',
    },
    navButton: {
      color: 'black',
      nextIcon: (color: string) => `"data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.5 17L14.5 12L9.5 7V17Z' fill='${encodeURIComponent(color)}'/%3E%3C/svg%3E%0A"`,

      prevIcon: (color: string) => `"data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M14.5 17L9.5 12L14.5 7V17Z' fill='${encodeURIComponent(color)}'/%3E%3C/svg%3E%0A"`,
      // tslint:enable:max-line-length
      backgroundSize: '100%'
    },
    monthLabel: {
      fontSize: 'heroSmall',
      fontWeight: '600',
    },
    weekdayLabel: {
      color: 'black',
      fontSize: 'bodySmall',
      fontWeight: '600',
      padding: { top: '2.5x', bottom: '1x' },
      border: { side: 'bottom', color: 'gray400' }
    },
    focusRing: {
      outline: 'none',
      boxShadow: { color: 'focus', inset: false, shadow: '0 0 0 3px' },
    },
    dayCell: {
      borderRadius: { topLeft: 'double', bottomLeft: 'double', topRight: 'double', bottomRight: 'double' },
      fontSize: 'body',
      padding: { top: '0.75x', bottom: '2.75x' },
      leadingZeros: true,
      selected: {
        color: 'white',
        backgroundColor: 'primaryPurple',
        fontWeight: '600',
        selectHover: {
          color: 'white',
          backgroundColor: 'secondaryPurple',
          fontWeight: '600',
        }
      },
      hover: {
        backgroundColor: 'primaryWhite',
        color: 'primaryPurple',
        fontWeight: '400',
      },
      disabled: {
        color: 'gray600'
      },
      due: {
        fontWeight: '400',
        backgroundColor: 'error10',
        color: 'error',
        dueText: {
          fontSize: 'bodySmall',
          fontWeight: '400',
          color: 'error',
          lineHeight: '1.4',
          hover: {
            fontSize: 'bodySmall',
            color: 'primaryPurple',
          },
        },
        hover: {
          backgroundColor: 'primaryWhite',
          color: 'primaryPurple',
        }
      },
    },
    overlay: {
      backgroundColor: 'white',
      boxShadow: { color: 'rgba(79, 94, 102, 0.16)', shadow: '0px 2px 4px 2px' },
      width: '30.8rem',
      padding: { horizontal: 'double', top: '1.5x', bottom: 'quadruple' },
      borderRadius: '0.625x',
    },
    dayPickerProps: {
      months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      weekdaysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    },
  },
});

export default theme;
